import { AuthService } from '@auth0/auth0-angular';
import { AdminAuthService } from '../services/auth/admin-auth.service';
import { SessionStorageKeyEnum } from '../services/session-storage/session-storage';

/**
 * sessionStorage keys holding per-visitor booking state (customer PII, payment
 * intent, private-event grant). Owned here so ui-core never imports calendar
 * libs; the calendar stores import these constants. Cleared on every logout so
 * the next visitor on a shared device doesn't inherit them (#3202). Also covers
 * the ui-core encrypted booking/reschedule blobs, their per-tab decryption key
 * (stored beside them, so leaving it defeats the encryption) and the checkout
 * PaymentIntent (#3242). TENANT_SLUG is deliberately kept: it is public.
 */
export const BOOKING_FLOW_STORAGE_KEY = 'booking-flow-state';
export const PRIVATE_ACCESS_STORAGE_KEY = 'private-access-state';
export const LOGOUT_CLEARED_SESSION_KEYS: readonly string[] = [
  BOOKING_FLOW_STORAGE_KEY,
  PRIVATE_ACCESS_STORAGE_KEY,
  SessionStorageKeyEnum.BOOKING_TOKEN,
  SessionStorageKeyEnum.RESCHEDULE_TOKEN,
  SessionStorageKeyEnum.BOOKING_SESSION_KEY,
  SessionStorageKeyEnum.PAYMENT_INTENT_TOKEN,
];

export interface PerformLogoutOptionsI {
  /** Where Auth0 returns the browser after logout. Defaults to this app's origin. */
  returnTo?: string;
}

function clearPersistedSessionKeys(): void {
  for (const key of LOGOUT_CLEARED_SESSION_KEYS) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // blocked storage must never prevent logout
    }
  }
}

/**
 * Single safe logout path shared by every "Log out" entry point.
 *
 * clearAdminTenantInfo() touches localStorage/sessionStorage, which can throw
 * (SecurityError in private mode, blocked storage, quota). It MUST NOT be able
 * to prevent the Auth0 logout — otherwise a thrown cleanup leaves an
 * authenticated session alive on a shared device. The try/finally guarantees
 * auth.logout() always runs.
 */
export function performLogout(auth: AuthService, adminAuth: AdminAuthService, options?: PerformLogoutOptionsI): void {
  try {
    adminAuth.clearAdminTenantInfo();
  } finally {
    clearPersistedSessionKeys();
    // #1257 — return to THIS app's own origin after logout, not Auth0's default
    // (which sent customers to the calendar app). Each app's origin must be in
    // Auth0 "Allowed Logout URLs". Mirrors the operator app's signOut().
    auth.logout({
      logoutParams: { returnTo: options?.returnTo ?? window.location.origin },
      openUrl: url => location.replace(url),
    });
  }
}
